import { AsyncOrSync } from 'vtils/types'
import { bindEvent, cloneDeepFast, last } from 'vtils'
import { sensorsDirective, sensorsFunTrack } from './sensor'
import { useContext } from '@/hooks'
import type { DirectiveBinding } from 'vue/types/options'
import type { Vue } from 'vue/types/vue'
import type { VueConstructor } from 'vue/types/umd'

export interface TrackData {
  /**
   * 事件类型
   * - click 点击
   * - load 页面加载
   * - exposure 曝光
   */
  type: 'click' | 'load' | 'exposure'
  /**
   * 页面路径
   */
  path?: string
  /**
   * 控件 ID
   */
  id: string
  /**
   * 控件名称
   */
  name: string
  /**
   * 控件内容
   */
  value?: string
  /**
   * 事件内容
   */
  content?: string
  /**
   * 是否曝光，默认 true
   */
  expose?: boolean
  /**
   * 商品数据，当在商品详情页等特定商品下时需传入此数据
   */
  goods?: Record<string, any>
  /**
   * 资源数据，资源位时需传入此参数
   */
  resource?: Record<string, any>
  /**
   * 资源位数据，需使用占位资源位需传入此参数
   */
  resourcePosition?: {
    /**
     * 资源位 ID
     */
    id: string
    /**
     * 资源位名称
     */
    name: string
  }
  /**
   * 标识数据，看需要传入
   */
  mark?: {
    /**
     * 标识 ID
     */
    id: string
    /**
     * 标识名称
     */
    name: string
  }
  /**
   * 活动数据
   */
  activity?: {
    /**
     * 活动 ID
     */
    id: string
    /**
     * 活动名称
     */
    name: string
  }
  /**
   * 元素索引，在列表中时，需传入此参数
   */
  index?: number
  /**
   * 是否禁用，默认 false
   */
  disabled?: boolean
  /**
   * 是否可点击，默认 false
   */
  clickable?: boolean
  /**
   * 是否使用新版埋点
   */
  new?: boolean
  /**
   * 新埋点 ID
   */
  newId?: string
  /**
   * 额外参数
   */
  extra?: Record<string, any>
  [key: string]: any
}

export class Track {
  // Vue 应用实例
  static app: Vue = null as any

  // 统计 SDK 是否准备好
  static ready = false

  // 确保统计 SDK 已准备好
  static ensureReady = async function () {
    if (Track.ready) return
    return new Promise<void>(resolve => {
      const timer = setInterval(() => {
        if (
          // @ts-ignore
          typeof Countly !== 'undefined' &&
          // @ts-ignore
          typeof anyEventPush !== 'undefined' &&
          // @ts-ignore
          typeof loadEventPush !== 'undefined' &&
          // @ts-ignore
          typeof getRoutePath !== 'undefined' &&
          // @ts-ignore
          typeof segmentationConfig !== 'undefined' &&
          // @ts-ignore
          segmentationConfig.device_id !== 'NONE' &&
          Track.app != null
        ) {
          clearInterval(timer)
          Track.ready = true
          resolve()
        }
      }, 60)
    })
  }

  // 准备数据
  static prepareData = function (data: TrackData) {
    return {
      ...(data.id && data.name
        ? {
            control_id: data.id,
            control_name: data.name,
            control_value: data.value || data.name,
          }
        : {}),
      ...(data.goods
        ? data.goods.isUniGoods
          ? {
              content_id: data.goods.id,
              content_name: data.goods.name,
              resource_id: data.goods.resourceId,
              resource_name: data.goods.resourceName,
              resource_position_id: data.goods.resourcePositionId,
              resource_position_name: data.goods.resourcePositionName,
              resource_position_no: data.index || 0,
            }
          : {
              content_id: data.goods.commodityId,
              content_name:
                data.goods.commodityTitle ||
                // 兼容订单下的商品
                data.goods.name,
              ...(data.goods.commodityResourceId
                ? {
                    resource_id: data.goods.commodityResourceId,
                    resource_name: data.goods.commodityTitle,
                    resource_position_id: '',
                    resource_position_name: data.name,
                    resource_position_no: data.index || 0,
                  }
                : {}),
            }
        : {}),
      ...(data.resource
        ? {
            resource_id: data.resource.id || data.resource.resourceId,
            resource_name: data.resource.title || data.resource.name || '',
            resource_position_id: data.resource.stateId || '',
            resource_position_name: data.resource.stateName || '',
            resource_position_no: data.index || 0,
          }
        : {}),
      ...(data.resourcePosition
        ? {
            resource_position_id: data.resourcePosition.id,
            resource_position_name: data.resourcePosition.name,
            resource_position_no: data.index || 0,
          }
        : {}),
      ...(data.mark
        ? {
            mark_id: data.mark.id,
            mark_name: data.mark.name,
          }
        : {}),
      ...(data.activity
        ? {
            activity_id: data.activity.id,
            activity_name: data.activity.name,
          }
        : {}),
      ...(data.content
        ? {
            content_name: data.content,
          }
        : {}),
      ...data.extra,
    }
  }

  // 推送事件
  static pushEvent = async function (data: TrackData) {
    await Track.ensureReady()
    const trackData = Track.prepareData(data)
    const { page, id, name } = await Track.injectMark()
    // @ts-ignore
    trackData.page_title ??= page
    trackData.mark_id ??= id
    trackData.mark_name ??= name
    // @ts-ignore
    anyEventPush(data.type, data.path, data.name, trackData)

    if (data.new !== false && (data.extra || data.new)) {
      sensorsFunTrack({
        event: data.newId || data.id,
        data: trackData,
      })
    }
  }

  static getFrom = () => {
    return (
      new URLSearchParams(location.href.split('#')[1]).get('track_from') || ''
    )
  }

  // App 上作为混入使用
  static App = {
    created() {
      Track.app = this as any
    },

    watch: {
      '$route.path': {
        immediate: true,
        async handler(this: any, toPath: string, fromPath: string) {
          await Track.ensureReady()

          this.$nextTick(async () => {
            const { page, id, name } = await Track.injectMark()

            // @ts-ignore
            loadEventPush(toPath, fromPath || '', Track.app.$route.query, {
              page_title: page,
              mark_id: id,
              mark_name: name,
            })
          })
        },
      },
    },
  }

  static install = function (
    Vue: VueConstructor,
    options: {
      appid: string
      prefix?: string
    },
  ) {
    Track.ensureReady().then(() => {
      // @ts-ignore
      countlyConfig.baseContextPath = options.appid
    })

    async function applyTrack(el: any, payload: DirectiveBinding) {
      await Track.ensureReady()

      let data: TrackData = payload.value

      // @ts-ignore
      if (data && !data.disabled) {
        const newData = cloneDeepFast(data)
        data = {
          ...data,
          name: `${options.prefix || ''}${data.name}`,
          // @ts-ignore
          path: el.__track_data__?.path || getRoutePath(),
        }

        el.__track_data__ = data

        // 兼容曝光事件
        if (data.expose !== false) {
          el.setAttribute('exposure-event', data.name)
          el.__trigger_dcb_track_exposure__ = () => {
            console.log(`埋点：曝光「${data.name}」`)
            Track.pushEvent({
              type: 'exposure',
              ...el.__track_data__,
              new: false,
            })
          }
        }

        const shouldListen = !el.dataset.track
        if (shouldListen) {
          el.dataset.track = '1'
          el.__track_dispose__ = bindEvent(el)(
            'click' as any,
            () => {
              console.log(`埋点：点击「${data.name}」`)
              Track.pushEvent({
                type: 'click',
                ...el.__track_data__,
                value: el.innerText.trim().replace(/\s+/g, ''),
                new: false,
              })
            },
            {
              // 必须使用捕获模式以在其他事件触发前处理（比如其他事件触发了页面跳转，就会导致问题）
              capture: true,
              // 使用被动模式告诉浏览器这不会产生阻塞行为
              passive: true,
            },
          )
          el.__track_dispose2__ = bindEvent(el)(
            'click' as any,
            () => {
              if (data.resource && data.clickable) {
                Vue.prototype.$handleResourceClick(data.resource)
              }
            },
            {
              passive: true,
            },
          )

          if (newData.extra || newData.new) {
            sensorsDirective({
              el: el,
              binding: {
                ...payload,
                value: {
                  event: newData.newId || newData.id,
                  data: Track.prepareData(newData),
                },
              },
            })
          }
        }
      }
    }

    function disposeTrack(el: any) {
      // 需延时一下，不然当遇到点击跳转时页面卸载把事件也卸载了就触发不了了
      setTimeout(() => {
        delete el.__track_data__
        el.__track_dispose__?.()
        el.__track_dispose2__?.()
        delete el.__track_dispose__
        delete el.__track_dispose2__
        delete el.__trigger_dcb_track_exposure__
      }, 0)
    }

    Vue.directive('track', {
      bind: applyTrack,
      update: applyTrack,
      componentUpdated: applyTrack,
      inserted: applyTrack,
      unbind: disposeTrack,
    })

    Vue.prototype.$track = class TrackApi {
      static push(data: TrackData) {
        return Track.pushEvent({
          ...data,
          type: 'click',
        })
      }

      static get from() {
        return Track.getFrom()
      }
    }
  }

  static provideMark(
    cb: () => AsyncOrSync<{
      page: string
      id: string
      name: string
    }>,
  ) {
    return {
      methods: {
        provideTrackMark: cb,
      },
    }
  }

  static useMark(
    cb: () => AsyncOrSync<{
      page: string
      id?: string
      name?: string
    }>,
    condition?: () => boolean,
  ) {
    const ctx = useContext()
    ;(ctx as any).provideTrackMark = async () => {
      if (typeof condition === 'function') {
        await new Promise<void>(resolve => {
          const off = ctx.$watch(
            condition,
            (value: boolean) => {
              if (value) {
                off?.()
                resolve()
              }
            },
            {
              immediate: true,
            },
          )
        })
      }
      return cb()
    }
  }

  static async injectMark(): Promise<{
    page: string
    id: string
    name: string
  }> {
    const currentRoute = Track.app.$route
    const matched = currentRoute.matched
    const currentPage = last(matched)!
    const currentPageInstance = currentPage.instances.default
    if (!currentPageInstance)
      return {
        page: '',
        id: '',
        name: '',
      }
    const provideTrackMark = (currentPageInstance as any).provideTrackMark
    const {
      page = 'NONE',
      id = 'NONE',
      name = 'NONE',
    } = typeof provideTrackMark === 'function' ? await provideTrackMark() : {}
    return {
      page,
      id,
      name,
    }
  }
}
